import { Icon } from "@iconify/react";
import { Avatar, Box, Rating, Stack, Typography, useTheme, alpha } from "@mui/material";

interface TestimonialProps {
  testimonial: string;
  name: string;
  position: string;
  rating: number;
}

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const TestimonialCard = ({
  testimonial,
  name,
  position,
  rating,
}: TestimonialProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: "relative",
        px: { xs: 1, sm: 4, md: 6 },
        pt: { xs: 2, sm: 3 },
        pb: { xs: 1, sm: 2 },
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Top-Left Quote Icon */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: -10, sm: -6 },
          left: { xs: -4, sm: 8 },
          color: theme.palette.primary.main,
          opacity: 0.14,
          pointerEvents: "none",
          lineHeight: 1,
        }}
      >
        <Icon icon="ri:double-quotes-l" fontSize={60} />
      </Box>

      {/* Testimonial Quote Text (At Top) */}
      <Typography
        sx={{
          fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
          lineHeight: { xs: 1.65, sm: 1.75 },
          color: theme.palette.grey[800],
          fontStyle: "italic",
          fontWeight: 400,
          maxWidth: 720,
          mb: 3.5,
          zIndex: 1,
          px: { xs: 0.5, sm: 2 },
        }}
      >
        “{testimonial}”
      </Typography>

      {/* Author Section */}
      <Stack
        direction="column"
        alignItems="center"
        spacing={1.2}
        sx={{ zIndex: 1 }}
      >
        <Avatar
          sx={{
            width: { xs: 48, sm: 54 },
            height: { xs: 48, sm: 54 },
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            fontWeight: 700,
            fontSize: { xs: "1rem", sm: "1.1rem" },
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.28)}`,
            border: `2px solid ${theme.palette.common.white}`,
          }}
        >
          {getInitials(name)}
        </Avatar>

        <Stack alignItems="center" spacing={0.25}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.05rem", sm: "1.15rem" },
                color: theme.palette.grey[900],
              }}
            >
              {name}
            </Typography>
            <Box
              component="span"
              sx={{
                color: theme.palette.primary.main,
                display: "inline-flex",
                alignItems: "center",
              }}
              title="Verified Client"
            >
              <Icon icon="solar:verified-check-bold" width={17} height={17} />
            </Box>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.grey[600],
              fontSize: { xs: "0.825rem", sm: "0.875rem" },
              fontWeight: 500,
            }}
          >
            {position}
          </Typography>
        </Stack>

        {/* 5-Star Rating (Below Name & Position) */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ pt: 0.5 }}
        >
          <Rating
            value={rating}
            readOnly
            precision={0.5}
            sx={{
              color: "#FFB400",
              fontSize: { xs: "1.25rem", sm: "1.4rem" },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              bgcolor: alpha("#FFB400", 0.12),
              color: "#B78103",
              px: 1,
              py: 0.2,
              borderRadius: 1,
              fontWeight: 700,
              fontSize: "0.75rem",
            }}
          >
            5.0
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export { TestimonialCard };
